// @flow
import { disbatch } from 'redux-act';

import { selectWelcomeOfferEnabled } from 'src/modules/app/selectors';
import { startSSE, closeEventSource } from 'src/modules/app/sse';

import type { GetState, Dispatch } from '../../../types';
import { selectCanLoadMore, selectPagination } from '../selectors/pagination';
import { selectSearchValue } from '../selectors/search';

import { receiveBundle, receiveBundlePricing } from './bundle';
import { set as setDiscount } from './discount';
import { set as setPagination, reset as resetPagination } from './pagination';
import {
  add as addResults,
  reset as resetResults,
  setAvailability,
  setBilling,
  setTimeoutErrors,
} from './results';
import {
  set as setSearch,
  setAlteredSearch,
  setLoading as setSearchLoading,
  setFirstResultLoading as setSearchFirstResultLoading,
  setError as setSearchError,
  setFilterFailed,
  setSearchUUID,
} from './search';
import { getSuggestURL } from './suggest';

interface ExtendedError extends Error {
  _error?: string;
  pagination?: any;
  search?: any;
}

type SSEEvent = {
  event:
    | 'pagination'
    | 'suggestions'
    | 'suggest_meta'
    | 'discount'
    | 'das'
    | 'das_failed'
    | 'billing'
    | 'billing_failed'
    | 'bundle_available'
    | 'bundle_pricing'
    | 'done'
    | 'error',
  data: string,
  ...
};
export type MakeSSEListeners = () => (
  dispatch: Dispatch,
  getState: GetState,
) => {
  [event: string]: void | (SSEEvent => mixed),
  ...
};
const makeSSEListeners: MakeSSEListeners =
  () => (dispatch: Dispatch, getState: GetState) => {
    function pagination(event: SSEEvent) {
      const currentPagination = selectPagination(getState());

      dispatch(
        // $FlowFixMe[not-an-array]
        setPagination({ ...currentPagination, ...JSON.parse(event.data) }),
      );
    }

    function results(event: SSEEvent) {
      disbatch(
        dispatch,
        // $FlowFixMe[not-an-array]
        addResults(JSON.parse(event.data)),
        // $FlowFixMe[not-an-array]
        setSearchFirstResultLoading(false),
      );
    }

    function availability(event: SSEEvent) {
      // $FlowFixMe[not-an-array]
      dispatch(setAvailability(JSON.parse(event.data)));
    }

    function billing(event: SSEEvent) {
      // $FlowFixMe[not-an-array]
      dispatch(setBilling(JSON.parse(event.data)));
    }
    function bundle_available(event: SSEEvent) {
      dispatch(receiveBundle(JSON.parse(event.data)));
    }
    function bundle_pricing(event: SSEEvent) {
      dispatch(receiveBundlePricing(JSON.parse(event.data)));
    }
    function suggest_meta(event: SSEEvent) {
      const meta = JSON.parse(event.data);
      if ('filter_failed' in meta) {
        // $FlowFixMe[not-an-array]
        dispatch(setFilterFailed(meta.filter_failed));
      }

      if ('search_uuid' in meta) {
        // $FlowFixMe[not-an-array]
        dispatch(setSearchUUID(meta.search_uuid));
      }
    }
    function discount(event: SSEEvent) {
      // $FlowFixMe[not-an-array]
      dispatch(setDiscount(JSON.parse(event.data)));
    }
    function done(/* event: SSEEvent */) {
      closeEventSource();
      disbatch(
        dispatch,
        // $FlowFixMe[not-an-array]
        setSearchLoading(false),
        // $FlowFixMe[not-an-array]
        setSearchFirstResultLoading(false),
        // $FlowFixMe[not-an-array]
        setTimeoutErrors(),
      );
    }
    function error(/* event: SSEEvent */) {
      // $FlowFixMe[not-an-array]
      dispatch(setSearchError(true));
      done();
    }

    function altered_search(event: SSEEvent) {
      // $FlowFixMe[not-an-array]
      dispatch(setAlteredSearch(JSON.parse(event.data)));
    }

    const discountEvents = selectWelcomeOfferEnabled(getState());

    return {
      pagination,
      suggestions: results,
      discount: discountEvents ? discount : undefined,
      das: availability,
      das_failed: availability,
      billing,
      billing_failed: billing,
      bundle_available,
      bundle_pricing,
      done,
      error,
      suggest_meta,
      altered_search,
    };
  };

/**
 * Load Suggestions for user's current search input using SSE AIOSuggest API.
 */
export const loadSuggestions =
  (): ((dispatch: Dispatch, getState: GetState) => void) =>
  (dispatch: Dispatch, getState: GetState) => {
    disbatch(
      dispatch,
      // $FlowFixMe[not-an-array]
      resetPagination(),
      // $FlowFixMe[not-an-array]
      resetResults(),
      // $FlowFixMe[not-an-array]
      setAlteredSearch(false),
      // $FlowFixMe[not-an-array]
      setFilterFailed(false),
      // $FlowFixMe[not-an-array]
      setSearchError(false),
      // $FlowFixMe[not-an-array]
      setSearchUUID(undefined),
    );

    const url = getSuggestURL(getState());
    if (url) {
      // $FlowFixMe[not-an-array]
      dispatch(setSearchLoading(true));
      // $FlowFixMe[not-an-array]
      dispatch(setSearchFirstResultLoading(true));

      dispatch(
        startSSE(
          url,
          // $FlowFixMe[incompatible-call] - flow doesnt like enum
          makeSSEListeners,
        ),
      );
    }
  };

/**
 * Load More Suggestions (next page) for user's search input using SSE AIOSuggest API.
 */
export const loadMoreSuggestions =
  (): ((dispatch: Dispatch, getState: GetState) => void) =>
  (dispatch: Dispatch, getState: GetState) => {
    const pagination = selectPagination(getState());
    const search = selectSearchValue(getState());

    const canLoadMore = selectCanLoadMore(getState());

    if (!pagination || !canLoadMore) {
      const error: ExtendedError = new Error(
        'Unable to load more suggestions than available.',
      );
      error.pagination = pagination;
      error.search = search;

      throw error;
    }

    const page = pagination.page + 1;
    // $FlowFixMe[not-an-array]
    dispatch(setPagination({ page }));

    const url = getSuggestURL(getState());
    if (url) {
      // $FlowFixMe[not-an-array]
      dispatch(setSearchFirstResultLoading(true));
      dispatch(
        startSSE(
          url,
          // $FlowFixMe[incompatible-call] - flow doesnt like enum
          makeSSEListeners,
        ),
      );
    }
  };

export const submitSearch =
  (search: string): (Dispatch => void) =>
  dispatch => {
    // $FlowFixMe[not-an-array]
    dispatch(setSearch(search));
    dispatch(loadSuggestions());
  };
